import { type FC, memo, useEffect, useRef, useState } from "react";
import { css, cva, cx } from "@stubhub/broadway-foundation/css";
import { Skeleton } from "@viagogo/core/ui";
import { Feature } from "@viagogo/feature";
import { FeatureSetEnum } from "src/shared/constants/FeatureSetEnum";
import { PE_KEYS } from "src/shared/constants/PageEventKeys";
import { PE_TYPE } from "src/shared/constants/PageEventTypes";
import { useAppContext } from "src/shared/context/AppContext";
import { useStorePageEvent } from "src/shared/hooks/useStorePageEvent";

const containerClass = css({
  position: "relative",
  width: "full",
  paddingBlockStart: "[56.25%]",
  height: "primitives.0",
});

const playerRecipe = cva({
  base: {
    position: "absolute",
    insetBlockStart: "primitives.0",
    insetInlineStart: "primitives.0",
    width: "full",
    height: "full",
    borderStartStartRadius: "element.default",
    borderStartEndRadius: "element.default",
    borderEndStartRadius: "element.default",
    borderEndEndRadius: "element.default",
  },
  variants: {
    loadState: {
      loading: {
        backgroundImage:
          "[linear-gradient(-60deg,var(--bway-colors-bg-surface-stronger),var(--bway-colors-bg-surface-stronger),var(--bway-colors-bg-surface-stronger),var(--bway-colors-bg-surface-strong),var(--bway-colors-bg-surface-stronger),var(--bway-colors-bg-surface-stronger),var(--bway-colors-bg-surface-strong))]",
        backgroundSize: "[400% 400%]",
        animationName: "[shimmer]",
        animationDuration: "[1.25s]",
        animationTimingFunction: "[linear]",
        animationIterationCount: "[infinite]",
      },
      ready: {},
    },
  },
  defaultVariants: {
    loadState: "ready",
  },
});

interface Props {
  videoId: string | null | undefined;
  className?: string;
}

/**
 * Embedded youtube video player
 */
export const EmbeddedYoutubePlayer: FC<Props> = memo(({ videoId, className }) => {
  const playerRef = useRef<YT.Player | null>(null);
  const appContext = useAppContext();
  const storePageEvent = useStorePageEvent();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let localStartTime = 0;

    const onPlayerError = (event: YT.OnErrorEvent) => {
      storePageEvent(PE_TYPE.LOGGING, PE_KEYS.PERFORMER_YOUTUBE_PLAYER_ERROR, String(event.data));
    };
    const onPlayerReady = () => {
      setIsLoading(false);
      storePageEvent(PE_TYPE.LOGGING, PE_KEYS.PERFORMER_YOUTUBE_PLAYER_READY, videoId ?? undefined);
    };
    const onPlayerStateChange = (event: YT.OnStateChangeEvent) => {
      if (event.data === window.YT.PlayerState.PLAYING && localStartTime === 0) {
        storePageEvent(PE_TYPE.LOGGING, PE_KEYS.PERFORMER_YOUTUBE_PLAYER_START, videoId ?? undefined);
        localStartTime = Date.now();
      } else if (event.data === window.YT.PlayerState.PAUSED || event.data === window.YT.PlayerState.ENDED) {
        // record how long video was playing
        const currentTime = Date.now();
        const elapsed = (currentTime - localStartTime) / 1000;
        localStartTime = 0;
        storePageEvent(PE_TYPE.LOGGING, PE_KEYS.PERFORMER_YOUTUBE_PLAYER_STOP, String(elapsed));
      }
    };

    const initializePlayer = () => {
      if (playerRef.current) {
        return;
      }
      playerRef.current = new window.YT.Player("player", {
        height: "390",
        width: "640",
        videoId: videoId ?? undefined,
        playerVars: {
          playsinline: 1,
          // player interface language
          hl: appContext.languageCode,
          // caption language
          cc_lang_pref: appContext.languageCode,
          // dont show player controls (only play/pause)
          controls: 0,
          // dont load annotations
          iv_load_policy: 3,
        },
        events: {
          onStateChange: onPlayerStateChange,
          onReady: onPlayerReady,
          onError: onPlayerError,
        },
      });
    };

    // create the <script> tag and load the youtube IFrame API
    const loadYouTubeIframeAPI = () => {
      const scriptTag = document.createElement("script");
      scriptTag.src = "https://www.youtube.com/iframe_api";
      const firstScriptTag = document.querySelector("script");
      firstScriptTag?.parentNode?.insertBefore(scriptTag, firstScriptTag);
      window.onYouTubeIframeAPIReady = initializePlayer;
    };

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- YouTube API is loaded asynchronously
    if (!window.YT && videoId) {
      loadYouTubeIframeAPI();
    }

    return () => {
      if (playerRef.current) {
        playerRef.current.destroy();
      }
    };
  }, [videoId, storePageEvent, appContext]);

  if (!videoId) {
    return null;
  }

  return (
    <div className={cx(containerClass, className)}>
      <Feature
        id={FeatureSetEnum.TMGlobalDSSkeleton}
        control={() => <div id="player" className={playerRecipe({ loadState: isLoading ? "loading" : "ready" })} />}
        variant={() => (
          <div id="player" className={playerRecipe()}>
            {!!isLoading && <Skeleton height="100%" width="100%" />}
          </div>
        )}
      />
    </div>
  );
});
EmbeddedYoutubePlayer.displayName = "EmbeddedYoutubePlayer";
